/**
 * 병의원 목록 화면.
 *
 * admin-ruda-web `hospitalInfo.js` 포팅.
 * - 서버 검색(병의원명·진료과)·영업상태·시/도·시/군/구 필터·페이지네이션
 * - 목록·카운트·엑셀은 같은 검색 조건을 쓴다
 * - 목록 API에 생성·삭제가 없어 등록/삭제 UI는 두지 않는다
 */
import {
  HOSPITAL_BUSINESS_STATUS_FILTER,
  HOSPITAL_REGION_ALL,
  getHospitalRegionValidationField,
  isHospitalExportEmptyError,
  sanitizeHospitalRegionFilter,
  toHospitalBusinessStatusFilter,
  toHospitalRegionParam,
} from '@/entities/hospital/api/hospital.api';
import {
  useExportHospitals,
  useHospitalList,
  useHospitalRegions,
} from '@/entities/hospital/hooks/useHospitals';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { FacilityCollectionRunButton } from '@/shared/components/FacilityCollectionRunButton';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { institutionDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { formatClosedOnDisplay } from '@/shared/utils/facilityClosure';
import {
  formatYkihoDisplay,
  getPartnerBusinessStatusBadgeVariant,
  getPartnerBusinessStatusLabel,
} from '@/shared/utils/partnerBusinessStatus';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Building2, CheckCircle2, Download, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

/** 화면 라벨은 한글, API 값은 OPEN/SUSPENDED/CLOSED. 전체는 파라미터 생략 */
const BUSINESS_STATUS_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '정상', value: HOSPITAL_BUSINESS_STATUS_FILTER.OPEN },
  { label: '휴업', value: HOSPITAL_BUSINESS_STATUS_FILTER.SUSPENDED },
  { label: '폐업', value: HOSPITAL_BUSINESS_STATUS_FILTER.CLOSED },
] as const;

type BusinessStatusFilter = (typeof BUSINESS_STATUS_OPTIONS)[number]['value'];

/** URL·레거시 한글 값을 라디오 value로 맞춘다 */
const toBusinessStatusUiValue = (value: string): BusinessStatusFilter => {
  const api = toHospitalBusinessStatusFilter(value);
  return api ?? 'all';
};

const INSTITUTION_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  hospitalSearchKey: '',
  deptSearchKey: '',
  businessStatus: 'all' as BusinessStatusFilter,
  sido: '',
  sigungu: '',
};

export const InstitutionsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    INSTITUTION_SEARCH_DEFAULTS,
  );

  // Text draft inputs: keep local useState for typing; sync from values via useEffect when URL changes
  const [nameInput, setNameInput] = useState(values.hospitalSearchKey);
  const [departmentInput, setDepartmentInput] = useState(values.deptSearchKey);
  const exportMutation = useExportHospitals();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportMutation.isPending;
  const {
    data: regions,
    isLoading: isRegionsLoading,
    isError: isRegionsError,
    refetch: refetchRegions,
  } = useHospitalRegions();

  useEffect(() => {
    setNameInput(values.hospitalSearchKey);
    setDepartmentInput(values.deptSearchKey);
  }, [values.hospitalSearchKey, values.deptSearchKey]);

  useEffect(() => {
    setGlobalLoading(isExporting, '병의원 목록 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const businessStatusUi = toBusinessStatusUiValue(values.businessStatus);
  const regionFilter = sanitizeHospitalRegionFilter(
    values.sido,
    values.sigungu,
    regions,
  );
  const selectedRegion = regions?.find(
    (region) => region.sido === regionFilter.sido,
  );
  const sigunguOptions = selectedRegion?.sigungu ?? [];
  const isSigunguDisabled =
    !regionFilter.sido ||
    sigunguOptions.length === 0 ||
    isRegionsLoading ||
    isRegionsError;
  const hasRegionInUrl =
    Boolean(toHospitalRegionParam(values.sido)) ||
    Boolean(toHospitalRegionParam(values.sigungu));
  const regionsSettled = regions != null || isRegionsError;
  const waitingForRegions = hasRegionInUrl && !regionsSettled;

  const search = useMemo(
    () => ({
      hospitalSearchKey: values.hospitalSearchKey || undefined,
      deptOfTreatmentSearchKey: values.deptSearchKey || undefined,
      // 전체는 파라미터 생략. 한글 URL 잔재는 enum으로 정규화
      businessStatusFilter: toHospitalBusinessStatusFilter(
        values.businessStatus,
      ),
      sido: regionFilter.sido || undefined,
      sigungu: regionFilter.sigungu || undefined,
    }),
    [values, regionFilter.sido, regionFilter.sigungu],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = useHospitalList({
    search,
    pageable,
    enabled: !waitingForRegions,
  });

  useEffect(() => {
    if (regions == null) return;
    const currentSido = toHospitalRegionParam(values.sido) ?? '';
    const currentSigungu = toHospitalRegionParam(values.sigungu) ?? '';
    if (
      regionFilter.sido === currentSido &&
      regionFilter.sigungu === currentSigungu
    ) {
      return;
    }
    setValues({
      sido: regionFilter.sido,
      sigungu: regionFilter.sigungu,
      page: 1,
    });
  }, [
    regionFilter.sido,
    regionFilter.sigungu,
    regions,
    setValues,
    values.sido,
    values.sigungu,
  ]);

  useEffect(() => {
    if (!isError || !error) return;
    const regionField = getHospitalRegionValidationField(error);
    if (regionField === 'sido') {
      setValues({ sido: '', sigungu: '', page: 1 });
    } else if (regionField === 'sigungu') {
      setValues({ sigungu: '', page: 1 });
    }
    handleApiError(error, toast);
    // setValues는 URL 변경마다 참조가 바뀌므로 넣으면 토스트가 반복된다
  }, [isError, error, toast]);

  const hospitals = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const showListLoading = isLoading || waitingForRegions;
  const hasFilters =
    Boolean(values.hospitalSearchKey) ||
    Boolean(values.deptSearchKey) ||
    businessStatusUi !== 'all' ||
    Boolean(regionFilter.sido) ||
    Boolean(regionFilter.sigungu);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      hospitalSearchKey: nameInput.trim(),
      deptSearchKey: departmentInput.trim(),
      page: 1,
    });
  };

  const handleBusinessStatusChange = (value: BusinessStatusFilter) => {
    setValues({
      businessStatus: value,
      page: 1,
    });
  };

  const handleSidoChange = (value: string) => {
    setValues({
      sido: value === HOSPITAL_REGION_ALL ? '' : value,
      // 시/도 변경·해제 시 시군구는 반드시 초기화한다
      sigungu: '',
      page: 1,
    });
  };

  const handleSigunguChange = (value: string) => {
    setValues({
      sigungu: value === HOSPITAL_REGION_ALL ? '' : value,
      page: 1,
    });
  };

  const handleExport = async () => {
    try {
      const archive = await exportMutation.mutateAsync(search);
      if (!archive.downloadUrl) {
        toast.info('다운로드할 병의원이 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        `병의원 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isHospitalExportEmptyError(e)) {
        toast.info('다운로드할 병의원이 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Building2}
        title="병의원 관리"
        description="병의원 목록 조회 및 영업 상태 관리"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || showListLoading || totalCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '처리 중...' : '엑셀 다운로드'}
            </Button>
            <FacilityCollectionRunButton />
          </>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label htmlFor="hospital-name">병의원명</Label>
            <Input
              id="hospital-name"
              placeholder="병의원명"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="w-48"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="department">진료과</Label>
            <Input
              id="department"
              placeholder="진료과"
              value={departmentInput}
              onChange={(e) => setDepartmentInput(e.target.value)}
              className="w-48"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="hospital-sido">시/도</Label>
            <Select
              value={regionFilter.sido || HOSPITAL_REGION_ALL}
              onValueChange={handleSidoChange}
              disabled={isRegionsLoading || isRegionsError}
            >
              <SelectTrigger id="hospital-sido" className="w-48">
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={HOSPITAL_REGION_ALL}>전체</SelectItem>
                {(regions ?? []).map((region) => (
                  <SelectItem key={region.sido} value={region.sido}>
                    {region.sido}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="hospital-sigungu">시/군/구</Label>
            <Select
              value={regionFilter.sigungu || HOSPITAL_REGION_ALL}
              onValueChange={handleSigunguChange}
              disabled={isSigunguDisabled}
            >
              <SelectTrigger id="hospital-sigungu" className="w-56">
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={HOSPITAL_REGION_ALL}>전체</SelectItem>
                {sigunguOptions.map((sigungu) => (
                  <SelectItem key={sigungu} value={sigungu}>
                    {sigungu}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isRegionsError ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void refetchRegions()}
            >
              지역 다시 불러오기
            </Button>
          ) : null}
          <Button type="submit" className="ruda-button">
            검색
          </Button>
        </FilterRow>

        <div className="space-y-2">
          <Label>영업 상태</Label>
          <RadioGroup
            value={businessStatusUi}
            onValueChange={(v) =>
              handleBusinessStatusChange(v as BusinessStatusFilter)
            }
            className="flex flex-wrap gap-4"
          >
            {BUSINESS_STATUS_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                htmlFor={`business-status-${opt.value}`}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`business-status-${opt.value}`}
                />
                {opt.label}
              </label>
            ))}
          </RadioGroup>
        </div>
      </FilterCard>

      <DataCard>
        {showListLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="병의원 목록을 불러오는 중..." />
          </div>
        ) : hospitals.length === 0 ? (
          <EmptyState
            description={
              hasFilters
                ? '검색 조건에 맞는 병의원이 없습니다.'
                : '등록된 병의원이 없습니다.'
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>병의원 ID</TableHead>
                  <TableHead>병의원명</TableHead>
                  <TableHead>병의원 구분</TableHead>
                  <TableHead>요양기관기호</TableHead>
                  <TableHead>담당자 전화번호</TableHead>
                  <TableHead>진료 과목</TableHead>
                  <TableHead>영업 상태</TableHead>
                  <TableHead>폐업일</TableHead>
                  <TableHead className="text-center">상태변경 이력</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hospitals.map((hospital) => (
                  <TableRow
                    key={hospital.clinicId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: institutionDetailPath(hospital.clinicId),
                      } as never)
                    }
                  >
                    <TableCell>{hospital.clinicId}</TableCell>
                    <TableCell className="font-medium">
                      {hospital.clinicName}
                    </TableCell>
                    <TableCell>{hospital.clinicTypeCode}</TableCell>
                    <TableCell>{formatYkihoDisplay(hospital.ykiho)}</TableCell>
                    <TableCell>{hospital.managerPhoneNumber || '-'}</TableCell>
                    <TableCell>
                      {hospital.medicalDepartments?.length
                        ? hospital.medicalDepartments.join(', ')
                        : '-'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge
                        variant={getPartnerBusinessStatusBadgeVariant(
                          hospital.businessStatusType,
                        )}
                      >
                        {getPartnerBusinessStatusLabel(
                          hospital.businessStatusType,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatClosedOnDisplay(hospital.closedOn)}
                    </TableCell>
                    <TableCell className="text-center">
                      {hospital.hasStatusHistory ? (
                        <CheckCircle2 className="mx-auto h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="mx-auto h-5 w-5 text-red-500" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
